import React from 'react';
import { ShieldCheck, Activity, Flame, Zap, Magnet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface RightSidebarProps {
  coins: any[];
  limit: number;
  setLimit: (val: number) => void;
  selectedSymbol: string;
  setSelectedSymbol: (val: string) => void;
  riskSettings: any;
  balance: { total: number; free: number };
  isPaperMode: boolean;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({
  coins, limit, setLimit, selectedSymbol, setSelectedSymbol, riskSettings, balance, isPaperMode
}) => {
  const { t } = useTranslation();

  // ✅ FIX: Защита от null/undefined (coins || [])
  const activeSignals = (coins || []).filter(c => {
    if (!c.signal || c.signal === 'NONE') return false;
    const s = c.signal.toUpperCase();
    return s.includes('BUY') || s.includes('SELL') || s.includes('LONG') || s.includes('SHORT');
  });

  const getSignalStyle = (signal: string) => {
    const s = signal.toUpperCase();

    if (s.includes('SNIPER')) {
        return { 
            color: 'text-purple-300', bg: 'bg-purple-500/20', border: 'border-purple-500/40', 
            label: 'SNIPER', icon: <Activity size={10} /> 
        };
    }
    if (s.includes('PANIC')) {
        return { 
            color: 'text-orange-300', bg: 'bg-orange-500/20', border: 'border-orange-500/40', 
            label: 'PANIC', icon: <Flame size={10} /> 
        };
    }
    if (s.includes('FVG')) {
        return { 
            color: 'text-yellow-300', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', 
            label: 'FVG GAP', icon: <Magnet size={10} /> 
        };
    }
    if (s.includes('BUY') || s.includes('LONG')) {
      return { 
          color: 'text-green-300', bg: 'bg-green-500/20', border: 'border-green-500/30', 
          label: 'BUY', icon: <Zap size={10} /> 
      };
    }
    if (s.includes('SELL') || s.includes('SHORT')) {
      return { 
          color: 'text-red-300', bg: 'bg-red-500/20', border: 'border-red-500/30', 
          label: 'SELL', icon: <Zap size={10} /> 
      };
    }
    return { color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30', label: 'HOLD', icon: null };
  };

  return (
    <aside className="w-96 border-l border-slate-800/50 bg-[#0f172a]/95 backdrop-blur-xl p-5 flex flex-col fixed right-0 top-0 h-full z-30 shadow-2xl transition-all duration-300">
      
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* --- ЗАГОЛОВОК --- */}
      <div className="mb-6 flex flex-col gap-4 shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center gap-2 text-white uppercase tracking-wider">
            <ShieldCheck className="text-blue-500" size={18} /> {t('rightSidebar.title') || 'AI SIGNALS'}
          </h3>
          <div className="flex flex-col items-end">
            <span className="text-[10px] text-emerald-400 font-mono flex items-center gap-1 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]"></div>
              {t('rightSidebar.liveScanner') || 'LIVE'}
            </span>
            {isPaperMode && (
               <span className="text-[9px] text-blue-400 font-bold opacity-80 mt-1 uppercase tracking-wider">{t('rightSidebar.demoBalance') || 'DEMO MODE'}</span>
            )}
          </div>
        </div>
        
        {/* Переключатель лимита */}
        <div className="flex bg-slate-900/80 p-1 rounded-xl border border-slate-800/50">
          {[10, 20, 30].map((val) => (
            <button
              key={val}
              onClick={() => setLimit(val)}
              className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all uppercase tracking-wide ${
                limit === val 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20' 
                  : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
              }`}
            >
              TOP {val}
            </button>
          ))}
        </div>
      </div>

      {/* --- СПИСОК КАРТОЧЕК --- */}
      <div className="flex-1 overflow-y-auto no-scrollbar pb-20 space-y-3">
        {activeSignals.length > 0 ? (
          activeSignals.slice(0, limit).map((coin: any) => {
            
            // --- БЕЗОПАСНАЯ МАТЕМАТИКА ---
            const riskPercent = Number(riskSettings?.riskPercent) || 1; 
            const balanceUsage = Number(riskSettings?.maxBalanceUsage) || 100;
            const takeProfit = Number(riskSettings?.takeProfit) || 6;
            const stopLoss = Number(riskSettings?.stopLoss) || 2;
            const leverage = Number(riskSettings?.leverage) || 5;

            // ✅ FIX: Используем реальный переданный баланс (даже для демо), а не хардкод 10000
            const calculationBalance = Number(balance?.total) > 0 ? Number(balance.total) : (isPaperMode ? 10000 : 0);
            
            const allocatedBalance = calculationBalance * (balanceUsage / 100);
            const riskAmountUsdt = allocatedBalance * (riskPercent / 100);
            
            const rewardRatio = (stopLoss > 0) ? (takeProfit / stopLoss) : 3;
            const estProfitUsdt = riskAmountUsdt * rewardRatio;

            // Динамическое плечо
            let displayLeverage = leverage;
            if (coin.volatility > 12) displayLeverage = 2;
            else if (coin.volatility > 7) displayLeverage = Math.min(leverage, 5);

            const isSelected = selectedSymbol === coin.symbol;
            const style = getSignalStyle(coin.signal);

            // ✅ FIX: Чистое имя монеты для отображения (убираем /USDT и :USDT)
            const displayName = coin.symbol.replace('/', '').split(':')[0].replace('USDT', '');

            return (
              <div
                key={coin.symbol}
                onClick={() => setSelectedSymbol(coin.symbol)}
                className={`w-full p-4 rounded-[20px] border cursor-pointer transition-all duration-200 group relative overflow-hidden ${
                  isSelected
                    ? 'bg-blue-600/10 border-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.15)]'
                    : 'bg-[#1e293b]/40 border-slate-800/50 hover:bg-[#1e293b] hover:border-slate-700'
                }`}
              >
                {/* 1. Верхняя строка */}
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-black text-white group-hover:text-blue-400 transition-colors tracking-tight">
                      {displayName}
                    </span>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${style.bg} ${style.color} ${style.border}`}>
                      {style.icon} {style.label}
                    </span>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${coin.change > 0 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    {coin.change > 0 ? '+' : ''}{coin.change.toFixed(2)}%
                  </span>
                </div>

                {/* 2. Средняя строка */}
                <div className="flex justify-between items-end mb-4 border-b border-slate-700/30 pb-3">
                  <div>
                    <span className="text-[9px] text-slate-500 uppercase block mb-0.5 tracking-tighter font-bold">{t('rightSidebar.price') || 'PRICE'}</span>
                    <span className="text-sm font-mono font-medium text-slate-200">
                      ${coin.price < 1 ? coin.price.toFixed(5) : coin.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] text-slate-500 uppercase block mb-0.5 tracking-tighter font-bold">AI SCORE</span>
                    <span className={`text-base font-black ${coin.score >= 80 ? 'text-blue-400' : coin.score >= 50 ? 'text-slate-300' : 'text-slate-500'}`}>
                      {coin.score.toFixed(0)}
                    </span>
                  </div>
                </div>

                {/* 3. RSI Bar */}
                {coin.rsi && (
                  <div className="mb-4 bg-slate-900/50 p-2 rounded-xl border border-slate-800/50">
                    <div className="flex justify-between text-[8px] uppercase font-bold text-slate-500 mb-1.5 px-1">
                      <span>RSI Strength</span>
                      <span className={coin.rsi > 70 ? 'text-red-400' : coin.rsi < 30 ? 'text-green-400' : 'text-blue-400'}>
                        {coin.rsi.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-700 ${
                          coin.rsi > 70 ? 'bg-gradient-to-r from-red-600 to-red-400' 
                          : coin.rsi < 30 ? 'bg-gradient-to-r from-green-400 to-green-600' 
                          : 'bg-gradient-to-r from-blue-600 to-blue-400'
                        }`}
                        style={{ width: `${coin.rsi}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* 4. Footer */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 bg-slate-900/80 px-2 py-1 rounded-lg border border-slate-700/50 shadow-inner">
                      <span className="text-[9px] text-slate-500 font-bold">LEV</span>
                      <span className="text-xs font-black text-slate-200">{displayLeverage}x</span>
                    </div>

                    <div className="flex items-center gap-3 text-[10px]">
                      <div className="flex flex-col items-end">
                        <span className="text-slate-600 font-bold uppercase text-[8px]">RISK</span>
                        <span className="font-mono text-red-400 font-bold">-${riskAmountUsdt.toFixed(1)}</span>
                      </div>
                      <div className="w-px h-4 bg-slate-800"></div>
                      <div className="flex flex-col items-end">
                        <span className="text-slate-600 font-bold uppercase text-[8px]">TARGET</span>
                        <span className="font-mono text-green-400 font-bold">+${estProfitUsdt.toFixed(1)}</span>
                      </div>
                    </div>
                </div>

                {/* Индикатор выбора */}
                {isSelected && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 shadow-[2px_0_15px_rgba(59,130,246,0.8)]"></div>
                )}
              </div>
            );
          })
        ) : (
          /* ПУСТОЕ СОСТОЯНИЕ */
          <div className="h-96 flex flex-col items-center justify-center text-slate-500 opacity-50 space-y-4">
            <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full animate-pulse"></div>
                <Activity className="animate-spin text-blue-500 relative z-10" size={40} />
            </div>
            <div className="text-center">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold block mb-2 text-slate-400">{t('rightSidebar.scanning') || 'SCANNING MARKET'}</span>
                <span className="text-[9px] text-slate-600 italic block max-w-[200px] leading-relaxed">
                    {t('rightSidebar.scanningDesc') || 'Searching for high probability setups using Technical Analysis...'}
                </span>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};